import i18n from '@/i18n'

const OPTIONAL = ['kicker', 'subline', 'bullets', 'countdown', 'info']
const ROLE = '(?:c[1-4]|acc)'
const PERCENT = '(?:100|[0-9]{1,2})'
const EXPRESSION = new RegExp(`^(?:${ROLE}|transparent|(?:tint|shade)\\(${ROLE},${PERCENT}\\)|mix\\(${ROLE},${ROLE},${PERCENT}\\))$`)

// Templates on the Slider content engine (Split Slider 3, Story, Reel): same slots, element
// limit and palette expressions. Mirrors App\Rules\AdStudio\SliderConfiguration::TEMPLATE_IDS.
export const SLIDER_ENGINE_TEMPLATE_IDS = ['split-slider-v3', 'story-v1', 'reel-v1']
const TEXT_SLOTS = ['text', 'accent', 'hlText', 'marker', 'bar']

export function isSliderV3(configuration) {
  return configuration?.schemaVersion === 'studio-editor-v2/ad-v4'
    && SLIDER_ENGINE_TEMPLATE_IDS.includes(configuration?.template?.id)
}

// Shared by the rendered toggles and the update path (including assistant edits).
export function sliderElementLimitReached(configuration, path) {
  if (!isSliderV3(configuration)) return false
  const match = /^data\.slides\.(\d+)\.elements\.(kicker|subline|bullets|countdown|info)\.enabled$/.exec(path)
  if (!match) return false
  const elements = configuration.data?.slides?.[Number(match[1])]?.elements || {}
  return !elements[match[2]]?.enabled
    && OPTIONAL.filter((key) => elements[key]?.enabled).length >= 2
}

export function validPaletteExpression(value, textSlot = false) {
  return typeof value === 'string' && value.trim() === value
    && (EXPRESSION.test(value) || (textSlot && value === 'auto'))
}

export function visibleText(value) {
  return String(value ?? '').replace(/<[^>]*>/g, '').replace(/\u00ad/g, '').replace(/\[\[|\]\]|\{\{|\}\}/g, '').trim()
}

export function validCountdownDeadline(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(value)) return false
  const day = new Date(`${value.slice(0, 10)}T00:00:00Z`)
  return Number.isFinite(Date.parse(value)) && Number.isFinite(day.getTime())
    && day.toISOString().slice(0, 10) === value.slice(0, 10)
}

export function validateSliderConfiguration(configuration) {
  const errors = {}
  if (!isSliderV3(configuration)) return errors
  const data = configuration.data || {}
  ;(data.slides || []).forEach((slide, index) => {
    const path = `data.slides.${index}.elements`
    const elements = slide.elements || {}
    const enabled = OPTIONAL.filter((key) => elements[key]?.enabled === true)
    if (!visibleText(elements.slogan?.text)) errors[`${path}.slogan.text`] = i18n.t('editorV2.core.slider.sloganRequired')
    if (enabled.length > 2) enabled.forEach((key) => { errors[`${path}.${key}.enabled`] = i18n.t('editorV2.core.slider.tooManyElements') })
    enabled.forEach((key) => {
      if (['kicker', 'subline', 'info'].includes(key) && !visibleText(elements[key]?.text)) errors[`${path}.${key}.text`] = i18n.t('editorV2.core.slider.elementTextRequired')
    })
    if (enabled.includes('countdown')) {
      const countdown = elements.countdown
      if (!validCountdownDeadline(countdown.endAt)) errors[`${path}.countdown.endAt`] = i18n.t('editorV2.core.slider.invalidCountdown')
    }
    if (enabled.includes('bullets')) {
      const items = elements.bullets.items
      const maximum = 4
      if (!Array.isArray(items) || items.length < 1 || items.length > maximum || items.some((item) => !visibleText(item))) errors[`${path}.bullets.items`] = i18n.t('editorV2.core.slider.bulletItems', { max: maximum })
    }
  })
  Object.entries(data.style?.elements || {}).forEach(([element, value]) => {
    Object.entries(value.skin || {}).forEach(([slot, expression]) => {
      if (!validPaletteExpression(expression, TEXT_SLOTS.includes(slot))) errors[`data.style.elements.${element}.skin.${slot}`] = i18n.t('editorV2.core.slider.invalidPaletteExpression')
    })
  })
  return errors
}
